import axios from 'axios';

// interface

import {
  FetchLogin,
  EmailCheck,
  PhoneCheck,
  GenerateOTP,
  ValidateOTP,
  CreateUser,
  ValidateInviteEmail,
  GenerateInviteOTP,
  ResetPassword,
  ValidateResetPassword,
  CreateUserRequestBody,
  UpdateUser,
  LoginWithResourceSecretType,
} from '@app/typings/services/authentication.d';
import { endpoint, headers } from '@app/utils/service';
import {
  clearToken,
  getInviteToken,
  getLineIdRegister,
  getUserId,
  getToken,
} from '@app/utils/localStorage';

export const signout = () => {
  clearToken();
};

export const fetchLogin: FetchLogin = (email, password, phone) => {
  return axios({
    url: endpoint('/auth/accessToken'),
    method: 'POST',
    data: {
      grant_type: 'password',
      email: email || undefined,
      password,
      phone: email ? undefined : phone,
    },
  });
};

export const emailAvailability: EmailCheck = (email) => {
  return axios({
    url: endpoint(`/identities/emails/${email}/availability`),
    method: 'HEAD',
  });
};

export const phoneAvailability: PhoneCheck = (phoneNumber) => {
  return axios({
    url: endpoint(`/identities/phones/${phoneNumber}/availability`),
    method: 'HEAD',
  });
};

export const generateOTP: GenerateOTP = (type, email, phoneNumber) => {
  return axios({
    url: endpoint(`/auth/challenge`),
    method: 'POST',
    data: {
      type: type,
      email: email ? email : undefined,
      phoneNumber: phoneNumber ? phoneNumber : undefined,
    },
  });
};

export const validateOTP: ValidateOTP = (sessionId, otp) => {
  return axios({
    url: endpoint(`/auth/validate`),
    method: 'POST',
    data: {
      sessionId: sessionId,
      otp: otp,
    },
  });
};

export const createUser: CreateUser = (
  email,
  password,
  phoneNumber,
  firstName,
  lastName,
  emailSessionId,
  phoneSessionId,
  username,
) => {
  const data: CreateUserRequestBody = {
    password: password,
    firstName: firstName,
    lastName: lastName,
    email: email,
    emailSessionId: emailSessionId,
    phone: phoneNumber,
    phoneSessionId: phoneSessionId,
    username: username,
  };

  if (getLineIdRegister()) {
    data['lineTokenRequest'] = {
      idToken: getLineIdRegister() ?? undefined,
    };
  }

  return axios({
    url: endpoint(`/users`),
    method: 'POST',
    data,
  });
};

export const validateInviteEmail: ValidateInviteEmail = (info) => {
  return axios({
    url: endpoint(`/auth/validateInvitationEmail`),
    method: 'POST',
    data: {
      info: info,
    },
  });
};

export const generateInviteOTP: GenerateInviteOTP = (phoneNumber) => {
  return axios({
    url: endpoint(`/auth/challengeInvitationPhone`),
    method: 'POST',
    ...headers({ Authorization: `Bearer ${getInviteToken()}` }, false),
    data: {
      phoneNumber,
    },
  });
};

export const validateInvitePhone: ValidateOTP = (sessionId, otp) => {
  return axios({
    url: endpoint(`/auth/validateInvitationPhone`),
    method: 'POST',
    ...headers({ Authorization: `Bearer ${getInviteToken()}` }, false),
    data: {
      sessionId: sessionId,
      otp: otp,
    },
  });
};

export const resetPassword: ResetPassword = (email, phone) => {
  return axios({
    url: endpoint(`/auth/challengePasswordReset`),
    method: 'POST',
    data: {
      email: email || undefined,
      phone: email ? undefined : phone,
    },
  });
};

export const validateResetPassword: ValidateResetPassword = (info, password) => {
  return axios({
    url: endpoint(`/auth/validatePasswordReset`),
    method: 'POST',
    data: {
      info,
      password,
    },
  });
};

export const updateUser: UpdateUser = (otp, sessionId, userId) => {
  return axios({
    url: endpoint(`/users/${userId || getUserId()}/phoneEmail`),
    method: 'PATCH',
    ...headers({ Authorization: `Bearer ${getToken()}` }, false),
    data: {
      otp: otp,
      sessionId,
    },
  });
};

export const loginWithResourceSecret: LoginWithResourceSecretType = (resourceSecret) => {
  return axios({
    url: endpoint('/auth/login-with-resource-secret'),
    method: 'POST',
    data: {
      resource_secret: resourceSecret,
    },
  });
};
